import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram, Youtube, Facebook, Loader2 } from "lucide-react";
import { CHURCH } from "@/lib/data";

// API URL - mesma origem (frontend e backend juntos na Hostinger)
const API = '/api';

const TABS = [
  { id: "contact", label: "Fale Conosco" },
  { id: "prayer", label: "Pedido de Oração" },
];

export default function Contact() {
  const [tab, setTab] = useState("contact");
  const [loading, setLoading] = useState(false);

  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [prayerForm, setPrayerForm] = useState({
    name: "",
    email: "",
    phone: "",
    request: "",
    is_anonymous: false,
  });

  const submitContact = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      if (response.data.success) {
        toast.success("Mensagem enviada!", {
          description: "Obrigado por entrar em contato. Em breve retornaremos.",
        });
        setContactForm({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        toast.error("Erro ao enviar", { description: response.data.message || "Tente novamente." });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.detail || "Tente novamente em instantes.";
      toast.error("Erro ao enviar", { description: String(msg) });
    } finally {
      setLoading(false);
    }
  };

  const submitPrayer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...prayerForm };
      if (!payload.email) delete payload.email;
      const response = await axios.post(`${API}/prayer`, payload);
      if (response.data.success) {
        toast.success("Pedido recebido", {
          description: "Nossa equipe estará intercedendo por você. Deus é fiel!",
        });
        setPrayerForm({ name: "", email: "", phone: "", request: "", is_anonymous: false });
      } else {
        toast.error("Erro ao enviar", { description: response.data.message || "Tente novamente." });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.detail || "Tente novamente em instantes.";
      toast.error("Erro ao enviar", { description: String(msg) });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#050505] border border-white/10 px-4 py-4 text-white placeholder:text-white/35 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors";

  return (
    <section
      id="contato"
      data-testid="contact-section"
      className="bg-[#050505] py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left — info */}
          <div className="lg:col-span-5">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-xs tracking-overline uppercase text-[#D4AF37] mb-6"
            >
              Contato
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-testid="contact-title"
              className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight"
            >
              Venha como você é. <br />
              <span className="italic text-white/80">Saia </span>
              <span className="text-[#D4AF37]">transformado</span>.
            </motion.h2>

            <p className="mt-8 text-white/65 leading-relaxed max-w-md">
              Estamos aqui para você. Envie uma mensagem, compartilhe um pedido
              de oração ou venha nos visitar pessoalmente.
            </p>

            <ul className="mt-12 space-y-6" data-testid="contact-info-list">
              <li className="flex items-start gap-4">
                <MapPin size={20} strokeWidth={1.5} className="mt-1 text-[#D4AF37]" />
                <div>
                  <p className="text-[10px] tracking-overline uppercase text-white/55 mb-1">
                    Endereço
                  </p>
                  <p className="text-white">{CHURCH.address}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={20} strokeWidth={1.5} className="mt-1 text-[#D4AF37]" />
                <div>
                  <p className="text-[10px] tracking-overline uppercase text-white/55 mb-1">
                    Telefone
                  </p>
                  <a
                    href={`tel:${CHURCH.phoneRaw}`}
                    className="text-white hover:text-[#D4AF37] transition-colors"
                    data-testid="contact-phone"
                  >
                    {CHURCH.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Mail size={20} strokeWidth={1.5} className="mt-1 text-[#D4AF37]" />
                <div>
                  <p className="text-[10px] tracking-overline uppercase text-white/55 mb-1">
                    E-mail
                  </p>
                  <a
                    href={`mailto:${CHURCH.email}`}
                    className="text-white hover:text-[#D4AF37] transition-colors"
                    data-testid="contact-email"
                  >
                    {CHURCH.email}
                  </a>
                </div>
              </li>
            </ul>

            <div className="mt-12 flex items-center gap-4">
              <a
                href={CHURCH.socials.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="social-instagram"
                className="w-12 h-12 border border-white/15 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-all"
              >
                <Instagram size={18} strokeWidth={1.5} />
              </a>
              <a
                href={CHURCH.socials.youtube.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                data-testid="social-youtube"
                className="w-12 h-12 border border-white/15 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-all"
              >
                <Youtube size={18} strokeWidth={1.5} />
              </a>
              <a
                href={CHURCH.socials.facebook.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="social-facebook"
                className="w-12 h-12 border border-white/15 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-all"
              >
                <Facebook size={18} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Right — form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 bg-[#0A0A0A] p-8 sm:p-12 border border-white/5"
            data-testid="contact-form-card"
          >
            <div
              className="flex border-b border-white/10 mb-10"
              data-testid="contact-tabs"
            >
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`flex-1 py-4 text-xs sm:text-sm uppercase tracking-overline transition-colors ${
                    tab === t.id
                      ? "text-[#D4AF37] border-b-2 border-[#D4AF37] -mb-px"
                      : "text-white/50 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "contact" ? (
              <form
                onSubmit={submitContact}
                className="space-y-6"
                data-testid="contact-form"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <input
                    type="text"
                    placeholder="Seu nome"
                    required
                    minLength={2}
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className={inputCls}
                    data-testid="contact-name-input"
                  />
                  <input
                    type="email"
                    placeholder="Seu e-mail"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className={inputCls}
                    data-testid="contact-email-input"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <input
                    type="tel"
                    placeholder="Telefone *"
                    required
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className={inputCls}
                    data-testid="contact-phone-input"
                  />
                  <input
                    type="text"
                    placeholder="Assunto"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    className={inputCls}
                    data-testid="contact-subject-input"
                  />
                </div>
                <textarea
                  placeholder="Sua mensagem"
                  required
                  minLength={4}
                  rows={6}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className={`${inputCls} resize-none`}
                  data-testid="contact-message-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-10 py-4 font-medium hover:bg-[#B3932E] transition-colors disabled:opacity-60"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  Enviar mensagem
                </button>
              </form>
            ) : (
              <form
                onSubmit={submitPrayer}
                className="space-y-6"
                data-testid="prayer-form"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <input
                    type="text"
                    placeholder={prayerForm.is_anonymous ? "Seu nome (opcional)" : "Seu nome"}
                    required={!prayerForm.is_anonymous}
                    minLength={prayerForm.is_anonymous ? undefined : 2}
                    value={prayerForm.name}
                    onChange={(e) => setPrayerForm({ ...prayerForm, name: e.target.value })}
                    className={inputCls}
                    data-testid="prayer-name-input"
                  />
                  <input
                    type="tel"
                    placeholder="Telefone (opcional)"
                    value={prayerForm.phone}
                    onChange={(e) => setPrayerForm({ ...prayerForm, phone: e.target.value })}
                    className={inputCls}
                    data-testid="prayer-phone-input"
                  />
                </div>
                <input
                  type="email"
                  placeholder="E-mail (opcional)"
                  value={prayerForm.email}
                  onChange={(e) => setPrayerForm({ ...prayerForm, email: e.target.value })}
                  className={inputCls}
                  data-testid="prayer-email-input"
                />
                <textarea
                  placeholder="Compartilhe seu pedido — estaremos orando por você."
                  required
                  minLength={4}
                  rows={6}
                  value={prayerForm.request}
                  onChange={(e) => setPrayerForm({ ...prayerForm, request: e.target.value })}
                  className={`${inputCls} resize-none`}
                  data-testid="prayer-request-input"
                />
                <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={prayerForm.is_anonymous}
                    onChange={(e) =>
                      setPrayerForm({ ...prayerForm, is_anonymous: e.target.checked })
                    }
                    className="w-4 h-4 accent-[#D4AF37]"
                    data-testid="prayer-anonymous-checkbox"
                  />
                  Manter este pedido em sigilo (visto apenas pela equipe pastoral).
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="prayer-submit-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-10 py-4 font-medium hover:bg-[#B3932E] transition-colors disabled:opacity-60"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  Enviar pedido
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
